import { Button, ButtonLink } from '@/components/ui/button';
import { useState  } from 'react';
import type {FormEvent} from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Page } from '@/components/layout/page';
import { DevNote } from '@/components/ui/dev-note';
import { Input } from '@/components/ui/field';
import { Alert, Card, CardBody, Container, Section } from '@/components/ui/primitives';
import { dashlite } from '@/lib/dashlite';
import { fieldErrors, isApiError, presentError } from '@/lib/errors';
import { paths } from '@/lib/paths';

/**
 * Réinitialisation du mot de passe — `POST /auth/reset-password`.
 *
 * Cette page est la **cible du lien envoyé par email**. Elle lit le token et
 * l'adresse dans la query string : `/reinitialiser-mot-de-passe?token=…&email=…`.
 *
 * Le token n'est jamais affiché ni stocké : il ne sert qu'à cette requête.
 * Un token invalide ou expiré renvoie `422 invalid_reset_token` — auquel cas
 * on propose simplement d'en redemander un.
 *
 * La confirmation du mot de passe est vérifiée **côté front** avant l'appel :
 * inutile de faire un aller-retour pour une faute de frappe.
 */
export default function ResetPasswordPage() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();

    const token = searchParams.get('token') ?? '';
    const emailFromLink = searchParams.get('email') ?? '';

    const [email, setEmail] = useState(emailFromLink);
    const [password, setPassword] = useState('');
    const [confirmation, setConfirmation] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);
    const [error, setError] = useState<unknown>(null);
    const [localError, setLocalError] = useState<string | null>(null);

    const fields = fieldErrors(error);

    async function handleSubmit(event: FormEvent): Promise<void> {
        event.preventDefault();
        setLocalError(null);

        if (password !== confirmation) {
            setLocalError('Les deux mots de passe saisis ne sont pas identiques.');

            return;
        }

        setSubmitting(true);
        setError(null);

        try {
            await dashlite.resetPassword({ email, token, password });
            setDone(true);

            // Petite pause pour laisser le message de succès s'afficher avant
            // de renvoyer vers la connexion.
            setTimeout(() => navigate(paths.login, { replace: true }), 2500);
        } catch (caught) {
            setError(caught);
        } finally {
            setSubmitting(false);
        }
    }

    if (!token) {
        return (
            <Page title="Lien invalide">
                <Section>
                    <Container className="max-w-md space-y-6">
                        <Alert tone="critical" title="Ce lien est incomplet">
                            Aucun jeton de réinitialisation n'a été trouvé dans l'adresse. Ouvrez le lien depuis
                            l'email que vous avez reçu, ou demandez-en un nouveau.
                        </Alert>

                        <div className="flex justify-center">
                            <ButtonLink to={paths.forgotPassword}>Demander un nouveau lien</ButtonLink>
                        </div>
                    </Container>
                </Section>
            </Page>
        );
    }

    return (
        <Page title="Nouveau mot de passe">
            <Section>
                <Container className="max-w-md space-y-6">
                    <div className="text-center">
                        <h1 className="font-display text-3xl tracking-tight">Choisir un mot de passe</h1>
                        <p className="text-ink-soft mt-2 text-sm">
                            Il vous servira à vous reconnecter et à retrouver votre accès.
                        </p>
                    </div>

                    {done ? (
                        <Alert tone="positive" title="Mot de passe enregistré">
                            Vous allez être redirigé vers la page de connexion.
                        </Alert>
                    ) : (
                        <Card>
                            <CardBody>
                                <form onSubmit={handleSubmit} className="space-y-5" noValidate>
                                    <Input
                                        label="Adresse email"
                                        type="email"
                                        required
                                        autoComplete="email"
                                        value={email}
                                        error={fields.email}
                                        onChange={(event) => setEmail(event.target.value)}
                                        // Pré-remplie depuis le lien : le
                                        // visiteur n'a normalement rien à
                                        // corriger, mais rien n'empêche de le
                                        // laisser le faire.
                                        readOnly={emailFromLink !== ''}
                                    />

                                    <Input
                                        label="Nouveau mot de passe"
                                        type="password"
                                        required
                                        autoFocus
                                        minLength={8}
                                        autoComplete="new-password"
                                        value={password}
                                        error={fields.password}
                                        onChange={(event) => setPassword(event.target.value)}
                                        hint="Huit caractères au minimum."
                                    />

                                    <Input
                                        label="Confirmation"
                                        type="password"
                                        required
                                        autoComplete="new-password"
                                        value={confirmation}
                                        error={localError ?? undefined}
                                        onChange={(event) => setConfirmation(event.target.value)}
                                    />

                                    {error !== null && Object.keys(fields).length === 0 && (
                                        <Alert tone="critical" title={presentError(error).message}>
                                            {presentError(error).hint}
                                            {isApiError(error, 'invalid_reset_token') && (
                                                <p className="mt-2">
                                                    <Link to={paths.forgotPassword} className="underline">
                                                        Demander un nouveau lien
                                                    </Link>
                                                </p>
                                            )}
                                        </Alert>
                                    )}

                                    <Button type="submit" size="lg" className="w-full" loading={submitting}>
                                        Enregistrer le mot de passe
                                    </Button>
                                </form>
                            </CardBody>
                        </Card>
                    )}

                    <DevNote title="Ce que fait cet écran" endpoints={['POST /auth/reset-password']}>
                        <p>
                            Le token et l'adresse viennent de la query string du lien reçu par email. L'appel envoie
                            les trois valeurs : <code className="font-mono">email</code>,{' '}
                            <code className="font-mono">token</code> et <code className="font-mono">password</code>.
                        </p>
                        <p>
                            Un token expiré renvoie <code className="font-mono">422 invalid_reset_token</code> : la
                            bonne réaction est de proposer d'en redemander un, pas d'afficher une erreur technique.
                        </p>
                    </DevNote>
                </Container>
            </Section>
        </Page>
    );
}
